/**
 * Portable Ice System Role Engine v1.
 *
 * Determines whether selected/owned products form a functionally complete
 * portable Garmin LiveScope ice setup. Does not recommend products, price,
 * or compute battery runtime.
 */

const {
  expandPackageContents,
  evaluateBuildCompatibility,
} = require('./compatibility');
const { getSystemTemplate, PORTABLE_LIVESCOPE_ICE_SLUG } = require('./systemTemplates');

const BUILD_STATUSES = Object.freeze([
  'COMPLETE',
  'INCOMPLETE',
  'NEEDS_REVIEW',
  'INCOMPATIBLE',
]);

const ROLE_ASSESSMENT_STATUSES = Object.freeze([
  'SATISFIED',
  'MISSING',
  'NEEDS_REVIEW',
  'NOT_APPLICABLE',
]);

const COMPONENT_SOURCES = Object.freeze(['SELECTED', 'OWNED', 'PACKAGE_CONTENT']);

function indexBySlug(rows) {
  const map = new Map();
  for (const row of rows || []) {
    if (row?.slug) map.set(row.slug, row);
  }
  return map;
}

function unique(values) {
  return [...new Set((values || []).filter(Boolean))];
}

function resolveToSlug(idOrSlug, catalog) {
  if (!idOrSlug) return null;
  const products = catalog.products || [];
  if (products.some((product) => product.slug === idOrSlug)) return idOrSlug;
  const byId = products.find((product) => product.id === idOrSlug);
  return byId ? byId.slug : idOrSlug;
}

function childrenByPackage(catalog) {
  const map = new Map();
  for (const row of catalog.packageContents || []) {
    if (!row?.packageSlug || !row.componentSlug) continue;
    if (!map.has(row.packageSlug)) map.set(row.packageSlug, []);
    map.get(row.packageSlug).push(row.componentSlug);
  }
  return map;
}

function addProvenance(list, entry) {
  const exists = list.some(
    (item) =>
      item.slug === entry.slug
      && item.source === entry.source
      && item.viaPackageSlug === entry.viaPackageSlug
  );
  if (!exists) list.push(entry);
}

function collectProvenance(selectedSlugs, ownedSlugs, catalog) {
  const productBySlug = indexBySlug(catalog.products);
  const children = childrenByPackage(catalog);
  const selectedSet = new Set(selectedSlugs);
  const ownedSet = new Set(ownedSlugs);
  const bySlug = new Map();

  function ensure(slug) {
    if (!bySlug.has(slug)) bySlug.set(slug, []);
    return bySlug.get(slug);
  }

  function walkPackage(packageSlug, viaPackageSlug, seen) {
    const visited = seen || new Set();
    if (visited.has(packageSlug)) return;
    visited.add(packageSlug);
    for (const child of children.get(packageSlug) || []) {
      addProvenance(ensure(child), {
        slug: child,
        source: 'PACKAGE_CONTENT',
        viaPackageSlug: viaPackageSlug || packageSlug,
      });
      const childProduct = productBySlug.get(child);
      if (childProduct?.entityKind === 'package') {
        walkPackage(child, child, visited);
      }
    }
  }

  for (const slug of selectedSlugs) {
    addProvenance(ensure(slug), { slug, source: 'SELECTED', viaPackageSlug: null });
    if (productBySlug.get(slug)?.entityKind === 'package') walkPackage(slug, slug);
  }
  for (const slug of ownedSlugs) {
    addProvenance(ensure(slug), { slug, source: 'OWNED', viaPackageSlug: null });
    if (productBySlug.get(slug)?.entityKind === 'package') walkPackage(slug, slug);
  }

  return { bySlug, selectedSet, ownedSet };
}

function productMatchesRuleSide(product, productSlug, familySlug) {
  if (!product) return false;
  if (productSlug && product.slug === productSlug) return true;
  if (familySlug && product.hardwareFamilySlug === familySlug) return true;
  return false;
}

function verifiedRulesBetween(subject, object, rules, relationshipType) {
  return (rules || []).filter((rule) => {
    if (rule.relationshipType !== relationshipType) return false;
    if (rule.verificationStatus !== 'VERIFIED') return false;
    return (
      productMatchesRuleSide(subject, rule.subjectProductSlug, rule.subjectHardwareFamilySlug)
      && productMatchesRuleSide(object, rule.objectProductSlug, rule.objectHardwareFamilySlug)
    );
  });
}

function verifiedRequiresAdapter(subject, rules) {
  return (rules || []).filter(
    (rule) =>
      rule.relationshipType === 'REQUIRES_ADAPTER'
      && rule.verificationStatus === 'VERIFIED'
      && productMatchesRuleSide(subject, rule.subjectProductSlug, rule.subjectHardwareFamilySlug)
  );
}

function physicalPairingStatus(sonars, poles, rules) {
  if (!poles.length || !sonars.length) {
    return { status: 'NOT_EVALUATED', unresolved: [] };
  }
  const unresolved = [];
  let anyIncompatible = false;
  let allVerifiedCompatible = true;
  for (const sonar of sonars) {
    for (const pole of poles) {
      const incompatible = verifiedRulesBetween(sonar, pole, rules, 'INCOMPATIBLE_WITH');
      const compatible = verifiedRulesBetween(sonar, pole, rules, 'COMPATIBLE_WITH');
      if (incompatible.length) {
        anyIncompatible = true;
        continue;
      }
      if (!compatible.length) {
        allVerifiedCompatible = false;
        unresolved.push({
          kind: 'POLE_TRANSDUCER',
          sonarSlug: sonar.slug,
          poleSlug: pole.slug,
          reason: 'No VERIFIED COMPATIBLE_WITH rule for this pole/transducer pair.',
        });
      }
    }
  }
  if (anyIncompatible) return { status: 'INCOMPATIBLE', unresolved };
  if (!allVerifiedCompatible) return { status: 'NEEDS_REVIEW', unresolved };
  return { status: 'VERIFIED', unresolved: [] };
}

function combineStatus(statuses) {
  if (statuses.includes('INCOMPATIBLE')) return 'INCOMPATIBLE';
  if (statuses.includes('INCOMPLETE')) return 'INCOMPLETE';
  if (statuses.includes('NEEDS_REVIEW')) return 'NEEDS_REVIEW';
  if (statuses.every((status) => status === 'COMPLETE')) return 'COMPLETE';
  return 'INCOMPLETE';
}

function assessTemplateRole(roleDef, expandedProducts, provenance, compatibility, rules) {
  const holders = expandedProducts.filter((product) => (product.roles || []).includes(roleDef.role));
  const satisfiedBy = [];
  for (const product of holders) {
    const sources = provenance.bySlug.get(product.slug) || [
      { slug: product.slug, source: 'SELECTED', viaPackageSlug: null },
    ];
    for (const source of sources) {
      satisfiedBy.push({
        slug: product.slug,
        name: product.name,
        source: source.source,
        viaPackageSlug: source.viaPackageSlug,
      });
    }
  }

  if (roleDef.requirementLevel === 'CONDITIONAL') {
    const enforceWhen = roleDef.conditionData?.enforceWhen;
    if (enforceWhen === 'VERIFIED_REQUIRES_ADAPTER') {
      const sonars = expandedProducts.filter((p) => (p.roles || []).includes('LIVE_SONAR'));
      const adapterRules = sonars.flatMap((sonar) => verifiedRequiresAdapter(sonar, rules));
      if (!adapterRules.length) {
        return {
          role: roleDef.role,
          requirementLevel: roleDef.requirementLevel,
          completenessScope: roleDef.completenessScope,
          status: 'NOT_APPLICABLE',
          satisfiedBy: [],
          notes: ['No VERIFIED REQUIRES_ADAPTER rule applies.'],
        };
      }
      if (holders.length) {
        return {
          role: roleDef.role,
          requirementLevel: roleDef.requirementLevel,
          completenessScope: roleDef.completenessScope,
          status: 'SATISFIED',
          satisfiedBy,
          notes: [],
        };
      }
      const unresolvedSku = adapterRules.some(
        (rule) => !rule.objectProductSlug && !rule.objectHardwareFamilySlug
      );
      return {
        role: roleDef.role,
        requirementLevel: roleDef.requirementLevel,
        completenessScope: roleDef.completenessScope,
        status: unresolvedSku ? 'NEEDS_REVIEW' : 'MISSING',
        satisfiedBy: [],
        notes: ['Verified adapter requirement is unsatisfied.'],
      };
    }
    if (enforceWhen === 'VERIFIED_REQUIREMENT') {
      const requiredConnectivity = (compatibility.pairResults || []).flatMap((pair) =>
        (pair.requirements || []).filter((row) => row.relationshipType === 'REQUIRES_ADAPTER')
      );
      if (!requiredConnectivity.length) {
        return {
          role: roleDef.role,
          requirementLevel: roleDef.requirementLevel,
          completenessScope: roleDef.completenessScope,
          status: 'NOT_APPLICABLE',
          satisfiedBy: [],
          notes: ['No verified additional connectivity requirement.'],
        };
      }
      const unsatisfied = requiredConnectivity.filter((row) => !row.satisfied);
      if (!unsatisfied.length) {
        return {
          role: roleDef.role,
          requirementLevel: roleDef.requirementLevel,
          completenessScope: roleDef.completenessScope,
          status: holders.length ? 'SATISFIED' : 'SATISFIED',
          satisfiedBy,
          notes: [],
        };
      }
      const missingExactProduct = unsatisfied.some((row) => !row.productSlug);
      return {
        role: roleDef.role,
        requirementLevel: roleDef.requirementLevel,
        completenessScope: roleDef.completenessScope,
        status: missingExactProduct ? 'NEEDS_REVIEW' : 'MISSING',
        satisfiedBy,
        notes: ['Verified connectivity requirement is unresolved or unsatisfied.'],
      };
    }
    if (enforceWhen === 'EXPLICIT_REQUIRED_HARDWARE') {
      return {
        role: roleDef.role,
        requirementLevel: roleDef.requirementLevel,
        completenessScope: roleDef.completenessScope,
        status: holders.length ? 'SATISFIED' : 'NOT_APPLICABLE',
        satisfiedBy,
        notes: holders.length
          ? []
          : ['No structured product/package data requires separate display mounting hardware.'],
      };
    }
  }

  if (holders.length) {
    return {
      role: roleDef.role,
      requirementLevel: roleDef.requirementLevel,
      completenessScope: roleDef.completenessScope,
      status: 'SATISFIED',
      satisfiedBy,
      notes: [],
    };
  }

  return {
    role: roleDef.role,
    requirementLevel: roleDef.requirementLevel,
    completenessScope: roleDef.completenessScope,
    status: 'MISSING',
    satisfiedBy: [],
    notes: [],
  };
}

function electronicsStatusFrom(roleByName, pairResults) {
  const core = ['LIVE_SONAR', 'DISPLAY', 'SONAR_PROCESSOR_INTERFACE'];
  const missingCore = core.filter((role) => roleByName.get(role)?.status === 'MISSING');
  const pairsWithBothSides = (pairResults || []).filter((pair) => pair.subject && pair.object);
  const incompatible = pairsWithBothSides.filter((pair) => pair.status === 'INCOMPATIBLE');
  const unknown = pairsWithBothSides.filter((pair) => pair.status === 'NOT_FULLY_VERIFIED');
  const missingVerifiedRequirement = (pairResults || []).some((pair) =>
    (pair.requirements || []).some((row) => !row.satisfied)
  );

  if (incompatible.length) return 'INCOMPATIBLE';
  if (missingCore.length || missingVerifiedRequirement) return 'INCOMPLETE';
  if (unknown.length) return 'NEEDS_REVIEW';
  if (pairsWithBothSides.length && pairsWithBothSides.every((pair) => pair.status === 'COMPATIBLE')) {
    return 'COMPLETE';
  }
  return 'INCOMPLETE';
}

/**
 * @param {{
 *   templateSlug?: string,
 *   selectedProductIds?: string[],
 *   ownedProductIds?: string[],
 *   catalog: object,
 *   rules?: object[],
 *   template?: object,
 * }} input
 */
function evaluateSystemBuild(input) {
  const catalog = input.catalog;
  const rules = input.rules || [];
  const template =
    input.template || getSystemTemplate(input.templateSlug || PORTABLE_LIVESCOPE_ICE_SLUG);
  if (!template) {
    return {
      template: { slug: input.templateSlug || null },
      overallStatus: 'NEEDS_REVIEW',
      electronicsStatus: 'NEEDS_REVIEW',
      iceReadyStatus: 'NEEDS_REVIEW',
      roles: [],
      missingRequiredRoles: [],
      expandedComponents: [],
      compatibilityResults: [],
      componentAssessments: [],
      unresolvedItems: [{ kind: 'TEMPLATE', reason: 'Unknown system template.' }],
    };
  }

  const selectedSlugs = unique(
    (input.selectedProductIds || input.selectedSlugs || []).map((id) => resolveToSlug(id, catalog))
  );
  const ownedSlugs = unique(
    (input.ownedProductIds || input.ownedSlugs || []).map((id) => resolveToSlug(id, catalog))
  );
  const combinedRoots = unique([...selectedSlugs, ...ownedSlugs]);
  const expansion = expandPackageContents(combinedRoots, catalog);
  const provenance = collectProvenance(selectedSlugs, ownedSlugs, catalog);
  const compatibility = evaluateBuildCompatibility({
    selectedSlugs: expansion.expandedSlugs,
    catalog,
    rules,
  });

  const roles = template.roles.map((roleDef) =>
    assessTemplateRole(roleDef, expansion.products, provenance, compatibility, rules)
  );
  const roleByName = new Map(roles.map((row) => [row.role, row]));

  const missingRequiredRoles = roles
    .filter(
      (row) =>
        row.requirementLevel === 'REQUIRED'
        && (row.status === 'MISSING' || row.status === 'NEEDS_REVIEW')
    )
    .map((row) => ({
      role: row.role,
      requirementLevel: row.requirementLevel,
      completenessScope: row.completenessScope,
      status: row.status,
    }));

  const missingCompatibilityRequirements = (compatibility.pairResults || []).flatMap((pair) =>
    (pair.requirements || [])
      .filter((row) => !row.satisfied)
      .map((row) => ({
        relationshipType: row.relationshipType,
        productSlug: row.productSlug,
        hardwareFamilySlug: row.hardwareFamilySlug,
        sonarSlug: pair.subject?.slug || null,
      }))
  );

  const sonars = expansion.products.filter((p) => (p.roles || []).includes('LIVE_SONAR'));
  const poles = expansion.products.filter((p) => (p.roles || []).includes('TRANSDUCER_POSITIONING'));
  const pairing = physicalPairingStatus(sonars, poles, rules);

  const electronicsStatus = electronicsStatusFrom(roleByName, compatibility.pairResults);

  const iceRoleNames = ['POWER_SOURCE', 'PORTABLE_PLATFORM', 'TRANSDUCER_POSITIONING'];
  const iceMissing = iceRoleNames.filter((role) => roleByName.get(role)?.status === 'MISSING');
  const conditionalBlocking = roles.filter(
    (row) =>
      row.requirementLevel === 'CONDITIONAL'
      && (row.status === 'MISSING' || row.status === 'NEEDS_REVIEW')
  );

  let iceReadyStatus = 'INCOMPLETE';
  if (electronicsStatus === 'INCOMPATIBLE' || pairing.status === 'INCOMPATIBLE') {
    iceReadyStatus = 'INCOMPATIBLE';
  } else if (electronicsStatus === 'INCOMPLETE' || iceMissing.length) {
    iceReadyStatus = 'INCOMPLETE';
  } else if (
    electronicsStatus === 'NEEDS_REVIEW'
    || pairing.status === 'NEEDS_REVIEW'
    || conditionalBlocking.length
    || expansion.cycles.length
  ) {
    iceReadyStatus = 'NEEDS_REVIEW';
  } else if (electronicsStatus === 'COMPLETE' && !iceMissing.length && pairing.status === 'VERIFIED') {
    iceReadyStatus = 'COMPLETE';
  } else if (electronicsStatus === 'COMPLETE' && !iceMissing.length && pairing.status === 'NOT_EVALUATED') {
    iceReadyStatus = 'INCOMPLETE';
  } else {
    iceReadyStatus = 'NEEDS_REVIEW';
  }

  const unresolvedItems = [];
  if (expansion.cycles.length) {
    unresolvedItems.push({
      kind: 'PACKAGE_CYCLE',
      reason: 'Circular package membership was detected and skipped.',
      cycles: expansion.cycles,
    });
  }
  unresolvedItems.push(...pairing.unresolved);
  for (const pair of compatibility.pairResults || []) {
    if (pair.status === 'NOT_FULLY_VERIFIED' && pair.subject && pair.object) {
      unresolvedItems.push({
        kind: 'COMPATIBILITY',
        sonarSlug: pair.subject.slug,
        displaySlug: pair.object.slug,
        reason: 'No matching VERIFIED compatibility rule.',
      });
    }
  }

  const overallStatus = (() => {
    const combined = combineStatus([electronicsStatus, iceReadyStatus]);
    if (expansion.cycles.length && combined !== 'INCOMPATIBLE') return 'NEEDS_REVIEW';
    return combined;
  })();

  const expandedComponents = expansion.expandedSlugs.map((slug) => {
    const product = expansion.products.find((row) => row.slug === slug);
    return {
      slug,
      name: product?.name || null,
      entityKind: product?.entityKind || null,
      roles: product?.roles || [],
      sources: provenance.bySlug.get(slug) || [],
    };
  });

  return {
    template: {
      slug: template.slug,
      name: template.name,
    },
    overallStatus,
    electronicsStatus,
    iceReadyStatus,
    roles,
    missingRequiredRoles,
    missingCompatibilityRequirements,
    expandedComponents,
    compatibilityResults: compatibility.pairResults,
    componentAssessments: compatibility.componentAssessments,
    unresolvedItems,
    selection: {
      selectedProductIds: selectedSlugs,
      ownedProductIds: ownedSlugs,
      expandedSlugs: expansion.expandedSlugs,
      cycles: expansion.cycles,
    },
  };
}

module.exports = {
  BUILD_STATUSES,
  ROLE_ASSESSMENT_STATUSES,
  COMPONENT_SOURCES,
  evaluateSystemBuild,
};
